import { AttributeType } from 'src/interfaces/Template.interface'
import {
  indexLatestSavedAttributesByField,
  SavedCategoryAttributeRow,
} from './categoryAttributeIdentity'

/**
 * Catalog rows from template `attributes[].attribute_field` (AI suggestion or saved GET).
 * Skips GET /api/platform-field-data-attributes when the UI already has field metadata.
 */

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const finiteId = (value: unknown): number | null => {
  if (value == null || value === '') return null
  const id = Number(value)
  return Number.isFinite(id) ? id : null
}

/** Leaf category id from a template `category_data` field (object or raw id). */
export const resolveSuggestionCategoryId = (categoryData: unknown): number | null => {
  if (categoryData == null) return null
  if (typeof categoryData === 'object') {
    return finiteId((categoryData as { id?: unknown }).id)
  }
  return finiteId(categoryData)
}

const isFullAttributeFieldObject = (value: unknown): boolean => {
  const field = asRecord(value)
  return field != null && finiteId(field.id) != null
}

export type BuildCategoryAttributesOptions = {
  /** Saved edit hydrate: any id-only `attribute_field` → empty catalog (fall back to poll). */
  strictSavedRows?: boolean
}

/**
 * Attribute field objects the mapping form can render.
 * Non-strict (AI apply): skips rows without full `attribute_field` metadata.
 * Strict (saved template): returns [] if any row has a numeric-only `attribute_field`.
 */
export const buildCategoryAttributesFromRows = (
  attributes: unknown,
  options?: BuildCategoryAttributesOptions
): AttributeType[] => {
  if (!Array.isArray(attributes)) return []

  const strict = options?.strictSavedRows === true

  if (strict) {
    if (attributes.length === 0) return []
    for (const row of attributes) {
      const record = asRecord(row)
      if (!record || !('attribute_field' in record)) continue
      const raw = record.attribute_field
      if (raw == null) continue
      if (!isFullAttributeFieldObject(raw)) return []
    }
    const latest = indexLatestSavedAttributesByField(
      attributes as SavedCategoryAttributeRow[]
    )
    const catalog: AttributeType[] = []
    for (const row of latest.values()) {
      const field = asRecord(row.attribute_field)
      const id = finiteId(field?.id)
      if (field == null || id == null) return []
      catalog.push(field as AttributeType)
    }
    return catalog
  }

  const seen = new Set<number>()
  const catalog: AttributeType[] = []

  for (const row of attributes) {
    const record = asRecord(row)
    const field = asRecord(record?.attribute_field)
    const id = finiteId(field?.id)
    if (field == null || id == null || seen.has(id)) continue
    seen.add(id)
    catalog.push(field as AttributeType)
  }

  return catalog
}

/** @deprecated Prefer `buildCategoryAttributesFromRows` */
export const buildCategoryAttributesFromSuggestion = buildCategoryAttributesFromRows

/** True only when preloaded catalog rows belong to the category currently selected. */
export const shouldRenderPreloadedCategoryAttributes = (input: {
  preloadedAttributes: unknown
  selectedCategoryId: number | null
  savedCategoryId: number | null
}): boolean => {
  if (!Array.isArray(input.preloadedAttributes) || input.preloadedAttributes.length === 0) {
    return false
  }
  if (input.selectedCategoryId == null || input.savedCategoryId == null) return false
  return Number(input.selectedCategoryId) === Number(input.savedCategoryId)
}

/** When true, skip GET /api/platform-field-data-attributes and render from preloaded rows. */
export const shouldSkipCategoryAttributeCatalogFetch = (input: {
  forceCatalog: boolean
  preloadedAttributes: unknown
  selectedCategoryId: number | null
  savedCategoryId: number | null
}): boolean => {
  if (input.forceCatalog) return false
  return shouldRenderPreloadedCategoryAttributes({
    preloadedAttributes: input.preloadedAttributes,
    selectedCategoryId: input.selectedCategoryId,
    savedCategoryId: input.savedCategoryId,
  })
}
